import { useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Users, Upload, Download, Link2, Copy, Search, X, UserCog, ExternalLink, Mail } from "lucide-react";
import { formatIntermittentName } from "@/lib/intermittent";
import { COUNTRIES } from "@/lib/countries";

const EMPTY_PERSON = {
  first_name: "", last_name: "", role: "", role_type: "technicien", phone: "", email: "", numero_ss: "", active: true, notes: "",
  code_societe: "AAEV", raison_sociale: "Agence Antoine Evénements", matricule: "",
  nom_naissance: "", pseudonyme: "", genre: "Madame",
  adresse: "", complement_adresse: "", code_postal: "", ville: "",
  code_pays: "FR", libelle_pays: "FRANCE",
  code_pays_nationalite: "FR", libelle_pays_nationalite: "FRANCE",
  titre_sejour: "", expiration_titre_sejour: "", refus_demat: "Non",
  date_naissance: "", lieu_naissance: "", departement_naissance: "",
  code_pays_naissance: "FR", libelle_pays_naissance: "FRANCE",
  numero_conges_spectacles: "", plus_65_ans: "Non", retraite: "Non",
  fiscalite: "Métropole", abattement: "inconnu",
  code_dernier_contrat: "", code_profession: "", libelle_profession: "", type_contrat: "Intermittent",
  date_visite_medicale: "", date_limite_prochaine_visite: "",
  date_entree: "", date_anciennete: "",
  compte_racine: "421", compte_terminaison: "", libelle_racine: "NAP", libelle_terminaison: "",
  mode_reglement: "Virement", iban: "", bic: "",
  niveau_confidentialite: "(aucun)", statut_handicape: "", statut_handicape_debut: "",
  solde_rtt: "0", solde_repos_comp: "0", solde_conges_a_prendre: "0", solde_conges_acquis: "0",
  nb_enfants: "0",
};

export default function IntermittentsPage() {
  const [intermittents, setIntermittents] = useState([]);
  const [selectedIds, setSelectedIds] = useState({});
  const [filters, setFilters] = useState({ name: "", type: "all", role: "", contact: "", status: "all" });

  const [personDialogOpen, setPersonDialogOpen] = useState(false);
  const [personForm, setPersonForm] = useState(EMPTY_PERSON);
  const [personEditingId, setPersonEditingId] = useState(null);
  const [personTab, setPersonTab] = useState("identity");

  const [publicOpen, setPublicOpen] = useState(false);
  const [publicLinks, setPublicLinks] = useState([]);
  const [newLinkForm, setNewLinkForm] = useState({ label: "", intermittent_id: "", active: true, type: "registration", slug: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/intermittents");
      setIntermittents(data);
    } catch (e) { toast.error(extractError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNewPerson = () => { setPersonForm(EMPTY_PERSON); setPersonEditingId(null); setPersonTab("identity"); setPersonDialogOpen(true); };
  const openEditPerson = (p) => { setPersonForm({ ...EMPTY_PERSON, ...p }); setPersonEditingId(p.id); setPersonTab("identity"); setPersonDialogOpen(true); };
  const savePerson = async () => {
    try {
      if (personEditingId) await api.patch(`/intermittents/${personEditingId}`, personForm);
      else await api.post("/intermittents", personForm);
      toast.success(personEditingId ? "Modifié" : "Ajouté");
      setPersonDialogOpen(false);
      load();
    } catch (e) { toast.error(extractError(e)); }
  };
  const removePerson = async (p) => {
    if (!confirm(`Supprimer ${formatIntermittentName(p)} et toutes ses déclarations ?`)) return;
    try { await api.delete(`/intermittents/${p.id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  const importFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/intermittents/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.created} créé(s), ${data.updated} mis à jour`);
      load();
    } catch (e) { toast.error(extractError(e)); }
  };
  const exportFile = async () => {
    try {
      const ids = Object.keys(selectedIds).filter(k => selectedIds[k]);
      const params = ids.length > 0 ? { ids: ids.join(",") } : {};
      const res = await api.get("/intermittents/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const today = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const yyyymmdd = `${today.getFullYear()}${pad(today.getMonth() + 1)}${pad(today.getDate())}`;
      const suffix = ids.length > 0 ? `-${ids.length}sel` : "";
      a.download = `Sal-AAEV-${yyyymmdd}${suffix}.txt`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(ids.length > 0 ? `${ids.length} fiche(s) exportée(s)` : "Toutes les fiches exportées");
    } catch (e) { toast.error(extractError(e)); }
  };

  const toggleSelectPerson = (id) => setSelectedIds(prev => {
    const next = { ...prev };
    if (next[id]) delete next[id]; else next[id] = true;
    return next;
  });
  const toggleSelectAll = () => {
    const allSelected = filtered.length > 0 && filtered.every(p => selectedIds[p.id]);
    if (allSelected) {
      // Only deselect currently-visible ones
      setSelectedIds(prev => {
        const next = { ...prev };
        filtered.forEach(p => { delete next[p.id]; });
        return next;
      });
    } else {
      setSelectedIds(prev => ({ ...prev, ...Object.fromEntries(filtered.map(p => [p.id, true])) }));
    }
  };
  const selectedCount = Object.values(selectedIds).filter(Boolean).length;

  const filtered = useMemo(() => {
    const norm = (s) => (s || "").toString().toLowerCase();
    const nq = norm(filters.name), rq = norm(filters.role), cq = norm(filters.contact);
    return intermittents.filter(p => {
      const full = `${p.first_name || ""} ${p.last_name || ""}`.toLowerCase();
      if (nq && !full.includes(nq) && !norm(p.last_name).includes(nq) && !norm(p.first_name).includes(nq)) return false;
      if (filters.type !== "all") {
        const t = p.role_type === "artiste" ? "artiste" : "technicien";
        if (t !== filters.type) return false;
      }
      if (rq && !norm(p.role).includes(rq)) return false;
      if (cq) {
        const contact = `${p.email || ""} ${p.phone || ""}`.toLowerCase();
        if (!contact.includes(cq)) return false;
      }
      if (filters.status === "active" && !p.active) return false;
      if (filters.status === "inactive" && p.active) return false;
      return true;
    });
  }, [intermittents, filters]);

  const hasActiveFilter = !!(filters.name || filters.role || filters.contact || filters.type !== "all" || filters.status !== "all");
  const resetFilters = () => setFilters({ name: "", type: "all", role: "", contact: "", status: "all" });

  const bulkDeleteSelected = async () => {
    const ids = Object.keys(selectedIds).filter(k => selectedIds[k]);
    if (ids.length === 0) return;
    if (!confirm(`Supprimer définitivement ${ids.length} intermittent(s) et toutes leurs déclarations ?`)) return;
    try {
      const { data } = await api.post("/intermittents/bulk-delete", { ids });
      toast.success(`${data.deleted} intermittent(s) supprimé(s)`);
      setSelectedIds({});
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const bulkSetType = async (role_type) => {
    const ids = Object.keys(selectedIds).filter(k => selectedIds[k]);
    if (ids.length === 0) return;
    try {
      const { data } = await api.post("/intermittents/bulk-update", { ids, updates: { role_type } });
      toast.success(`${data.modified} fiche(s) passée(s) en ${role_type === "artiste" ? "Artiste" : "Technicien"}`);
      setSelectedIds({});
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const loadPublicLinks = async () => {
    try {
      const { data } = await api.get("/public-links");
      setPublicLinks(data);
    } catch (e) { toast.error(extractError(e)); }
  };
  const openPublicLinks = () => { loadPublicLinks(); setPublicOpen(true); };
  const createPublicLink = async () => {
    try {
      const payload = {
        label: newLinkForm.label,
        intermittent_id: newLinkForm.intermittent_id || null,
        active: newLinkForm.active,
        type: "registration",
        slug: newLinkForm.slug || null,
      };
      const { data } = await api.post("/public-links", payload);
      await navigator.clipboard.writeText(data.url).catch(() => {});
      toast.success("Lien créé et copié");
      setNewLinkForm({ label: "", intermittent_id: "", active: true, type: "registration", slug: "" });
      loadPublicLinks();
    } catch (e) { toast.error(extractError(e)); }
  };
  const editSlug = async (l) => {
    const suggested = l.slug || "";
    const raw = window.prompt(
      "URL personnalisée (a-z, 0-9, tirets — 3 à 60 caractères). Laissez vide pour utiliser le token par défaut.",
      suggested
    );
    if (raw === null) return; // annulation
    try {
      await api.patch(`/public-links/${l.token}`, { slug: raw.trim() });
      toast.success(raw.trim() ? "URL personnalisée mise à jour" : "URL personnalisée retirée");
      loadPublicLinks();
    } catch (e) { toast.error(extractError(e)); }
  };
  const toggleLinkActive = async (l) => {
    try {
      await api.patch(`/public-links/${l.token}`, { active: !l.active });
      loadPublicLinks();
    } catch (e) { toast.error(extractError(e)); }
  };
  const removePublicLink = async (token) => {
    if (!confirm("Supprimer ce lien ?")) return;
    try { await api.delete(`/public-links/${token}`); toast.success("Supprimé"); loadPublicLinks(); }
    catch (e) { toast.error(extractError(e)); }
  };
  const copyLink = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Lien copié"); }
    catch { toast.error("Copie impossible"); }
  };
  // Dialogue "Lien public" — inscription intermittent
  const INSCRIPTION_URL = "https://compagnon.antoineevenements.fr/declaration/inscription-intermittent";
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const openInscriptionLink = () => { setEmailTo(""); setLinkDialogOpen(true); };
  const sendByEmail = async () => {
    const email = (emailTo || "").trim();
    if (!email || !email.includes("@")) { toast.error("Email invalide"); return; }
    try {
      await api.post("/intermittents/send-inscription-email", { to_email: email });
      toast.success(`Lien envoyé à ${email}`);
      setLinkDialogOpen(false);
    } catch (e) {
      toast.error(extractError(e) || "Échec de l'envoi");
    }
  };

  return (
    <div className="space-y-6" data-testid="intermittents-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Intermittents</div>
          <h1 className="font-brand text-3xl md:text-4xl tracking-tight">Fiche des intermittents</h1>
          <p className="text-sm text-muted-foreground mt-1">Base des artistes et techniciens intermittents de l'agence.</p>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <input type="file" id="import-input" accept=".txt,.csv,.tsv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) importFile(f); e.target.value = ""; }} data-testid="import-file-input" />
          <Button variant="outline" className="rounded-full" onClick={() => document.getElementById("import-input").click()} data-testid="import-btn"><Upload className="h-4 w-4 mr-2" /> Importer</Button>
          {selectedCount > 0 && (
            <>
              <Button variant="outline" className="rounded-full" onClick={() => bulkSetType("technicien")} data-testid="bulk-set-technicien">
                <UserCog className="h-4 w-4 mr-2" /> → Technicien ({selectedCount})
              </Button>
              <Button variant="outline" className="rounded-full" onClick={() => bulkSetType("artiste")} data-testid="bulk-set-artiste">
                <UserCog className="h-4 w-4 mr-2" /> → Artiste ({selectedCount})
              </Button>
              <Button
                variant="outline"
                className="rounded-full text-destructive border-destructive/40 hover:bg-destructive/10"
                onClick={bulkDeleteSelected}
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" /> Supprimer la sélection ({selectedCount})
              </Button>
            </>
          )}
          <Button variant="outline" className="rounded-full" onClick={exportFile} data-testid="export-btn">
            <Download className="h-4 w-4 mr-2" /> Exporter{selectedCount > 0 ? ` (${selectedCount})` : ""}
          </Button>
          <Button variant="outline" className="rounded-full" onClick={openInscriptionLink} data-testid="public-link-btn"><Link2 className="h-4 w-4 mr-2" /> Lien public</Button>
          <Button className="rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-glow active:scale-95" onClick={openNewPerson} data-testid="add-person-btn"><Plus className="h-4 w-4 mr-2" /> Ajouter</Button>
        </div>
      </header>

      <Card className="border-border/60 shadow-warm"><CardContent className="p-0">
        <div className="flex flex-wrap items-end gap-3 p-3 border-b border-border/60 bg-muted" data-testid="intermittent-filters">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1 pt-2 pb-2">
            <Search className="h-3 w-3" /> Filtres
          </div>
          <div className="flex-1 min-w-[160px]">
            <Label className="text-xs">Nom</Label>
            <Input value={filters.name} onChange={e => setFilters(f => ({ ...f, name: e.target.value }))} placeholder="Nom ou prénom" data-testid="filter-name" />
          </div>
          <div className="min-w-[160px]">
            <Label className="text-xs">Type</Label>
            <Select value={filters.type} onValueChange={v => setFilters(f => ({ ...f, type: v }))}>
              <SelectTrigger data-testid="filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="technicien">Technicien</SelectItem>
                <SelectItem value="artiste">Artiste</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[160px]">
            <Label className="text-xs">Poste</Label>
            <Input value={filters.role} onChange={e => setFilters(f => ({ ...f, role: e.target.value }))} placeholder="Régisseur, Danseur..." data-testid="filter-role" />
          </div>
          <div className="flex-1 min-w-[160px]">
            <Label className="text-xs">Contact</Label>
            <Input value={filters.contact} onChange={e => setFilters(f => ({ ...f, contact: e.target.value }))} placeholder="Email ou téléphone" data-testid="filter-contact" />
          </div>
          <div className="min-w-[140px]">
            <Label className="text-xs">Statut</Label>
            <Select value={filters.status} onValueChange={v => setFilters(f => ({ ...f, status: v }))}>
              <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="active">Actifs</SelectItem>
                <SelectItem value="inactive">Inactifs</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="text-xs text-muted-foreground tabular-nums pb-2">
            <span className="font-semibold text-foreground">{filtered.length}</span> / {intermittents.length}
          </div>
          {hasActiveFilter && (
            <Button variant="ghost" size="sm" className="rounded-full mb-1" onClick={resetFilters} data-testid="filter-reset">
              <X className="h-3.5 w-3.5 mr-1" /> Réinitialiser
            </Button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/60">
                <th className="py-3 px-3 font-medium w-8">
                  <Checkbox
                    checked={filtered.length > 0 && filtered.every(p => selectedIds[p.id])}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-persons"
                  />
                </th>
                <th className="py-3 px-5 font-medium">Nom</th>
                <th className="py-3 px-5 font-medium">Type</th>
                <th className="py-3 px-5 font-medium">Poste</th>
                <th className="py-3 px-5 font-medium">Contact</th>
                <th className="py-3 px-5 font-medium">Statut</th>
                <th className="py-3 px-5 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-muted-foreground"><Users className="h-8 w-8 mx-auto mb-2 opacity-50" />{intermittents.length === 0 ? "Aucun intermittent" : "Aucun intermittent ne correspond aux filtres"}</td></tr>}
              {filtered.map(p => (
                <tr key={p.id} className="border-b border-border/40 hover:bg-muted fade-up" data-testid={`person-row-${p.id}`}>
                  <td className="py-3 px-3">
                    <Checkbox
                      checked={!!selectedIds[p.id]}
                      onCheckedChange={() => toggleSelectPerson(p.id)}
                      data-testid={`select-person-${p.id}`}
                    />
                  </td>
                  <td className="py-3 px-5">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold">
                        {p.last_name?.[0]}{p.first_name?.[0]}
                      </div>
                      <div className="font-medium cursor-pointer" onClick={() => openEditPerson(p)}>{formatIntermittentName(p)}</div>
                    </div>
                  </td>
                  <td className="py-3 px-5">
                    <Badge
                      variant="outline"
                      className={`rounded-full text-[10px] ${p.role_type === "artiste" ? "bg-accent/40 text-accent-foreground border-accent" : "bg-primary/10 text-primary border-primary/40"}`}
                      data-testid={`person-type-${p.id}`}
                    >
                      {p.role_type === "artiste" ? "Artiste" : "Technicien"}
                    </Badge>
                  </td>
                  <td className="py-3 px-5 text-muted-foreground">{p.role || "—"}</td>
                  <td className="py-3 px-5 text-muted-foreground">
                    <div className="text-xs">{p.email || "—"}</div>
                    <div className="text-xs">{p.phone || ""}</div>
                  </td>
                  <td className="py-3 px-5">
                    {p.active ? <Badge className="bg-secondary text-secondary-foreground border-secondary rounded-full">Actif</Badge> : <Badge variant="outline" className="rounded-full">Inactif</Badge>}
                  </td>
                  <td className="py-3 px-5 text-right">
                    <Button variant="ghost" size="icon" onClick={() => openEditPerson(p)}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="text-destructive hover:bg-destructive/10" onClick={() => removePerson(p)}><Trash2 className="h-4 w-4" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      {/* Person dialog */}
      <Dialog open={personDialogOpen} onOpenChange={setPersonDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{personEditingId ? "Modifier l'intermittent" : "Nouvel intermittent"}</DialogTitle></DialogHeader>
          <Tabs value={personTab} onValueChange={setPersonTab}>
            <TabsList className="bg-muted">
              <TabsTrigger value="identity" data-testid="p-tab-identity">Identité</TabsTrigger>
              <TabsTrigger value="address" data-testid="p-tab-address">Adresse</TabsTrigger>
              <TabsTrigger value="birth" data-testid="p-tab-birth">Naissance</TabsTrigger>
              <TabsTrigger value="paye" data-testid="p-tab-paye">Paye</TabsTrigger>
              <TabsTrigger value="bank" data-testid="p-tab-bank">Banque</TabsTrigger>
              <TabsTrigger value="docs" data-testid="p-tab-docs">Documents</TabsTrigger>
            </TabsList>

            <TabsContent value="identity" className="mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label>Prénom *</Label><Input value={personForm.first_name} onChange={e => setPersonForm({ ...personForm, first_name: e.target.value })} data-testid="p-first" /></div>
                <div><Label>Nom *</Label><Input value={personForm.last_name} onChange={e => setPersonForm({ ...personForm, last_name: e.target.value })} data-testid="p-last" /></div>
                <div><Label>Matricule (paye)</Label><Input value={personForm.matricule} onChange={e => setPersonForm({ ...personForm, matricule: e.target.value })} placeholder="acm" data-testid="p-matricule" /></div>
                <div><Label>Genre</Label>
                  <Select value={personForm.genre} onValueChange={v => setPersonForm({ ...personForm, genre: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Madame">Madame</SelectItem><SelectItem value="Monsieur">Monsieur</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Nom de naissance</Label><Input value={personForm.nom_naissance} onChange={e => setPersonForm({ ...personForm, nom_naissance: e.target.value })} /></div>
                <div><Label>Pseudonyme</Label><Input value={personForm.pseudonyme} onChange={e => setPersonForm({ ...personForm, pseudonyme: e.target.value })} /></div>
                <div><Label>Poste / fonction</Label><Input value={personForm.role} onChange={e => setPersonForm({ ...personForm, role: e.target.value })} placeholder="Artiste chorégraphique..." data-testid="p-role" /></div>
                <div>
                  <Label>Type</Label>
                  <Select value={personForm.role_type || "technicien"} onValueChange={v => setPersonForm({ ...personForm, role_type: v })}>
                    <SelectTrigger data-testid="p-role-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="technicien">Technicien</SelectItem>
                      <SelectItem value="artiste">Artiste</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Libellé profession (paye)</Label><Input value={personForm.libelle_profession} onChange={e => setPersonForm({ ...personForm, libelle_profession: e.target.value })} /></div>
                <div><Label>Email</Label><Input type="email" value={personForm.email} onChange={e => setPersonForm({ ...personForm, email: e.target.value })} data-testid="p-email" /></div>
                <div><Label>Téléphone</Label><Input value={personForm.phone} onChange={e => setPersonForm({ ...personForm, phone: e.target.value })} data-testid="p-phone" /></div>
                <div className="md:col-span-2 flex items-center gap-2">
                  <Checkbox id="active-cb" checked={personForm.active} onCheckedChange={v => setPersonForm({ ...personForm, active: !!v })} data-testid="p-active" />
                  <Label htmlFor="active-cb" className="cursor-pointer">Intermittent actif</Label>
                </div>
                <div className="md:col-span-2"><Label>Notes</Label><Textarea rows={3} value={personForm.notes} onChange={e => setPersonForm({ ...personForm, notes: e.target.value })} /></div>
              </div>
            </TabsContent>

            <TabsContent value="address" className="mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2"><Label>Adresse</Label><Input value={personForm.adresse} onChange={e => setPersonForm({ ...personForm, adresse: e.target.value })} /></div>
                <div className="md:col-span-2"><Label>Complément d'adresse</Label><Input value={personForm.complement_adresse} onChange={e => setPersonForm({ ...personForm, complement_adresse: e.target.value })} /></div>
                <div><Label>Code postal</Label><Input value={personForm.code_postal} onChange={e => setPersonForm({ ...personForm, code_postal: e.target.value })} /></div>
                <div><Label>Ville</Label><Input value={personForm.ville} onChange={e => setPersonForm({ ...personForm, ville: e.target.value })} /></div>
                <div><Label>Pays</Label><Input list="countries-list" value={personForm.libelle_pays} onChange={e => setPersonForm({ ...personForm, libelle_pays: e.target.value.toUpperCase() })} /></div>
                <div><Label>Nationalité</Label><Input list="countries-list" value={personForm.libelle_pays_nationalite} onChange={e => setPersonForm({ ...personForm, libelle_pays_nationalite: e.target.value.toUpperCase() })} /></div>
                <div><Label>Titre de séjour</Label><Input value={personForm.titre_sejour} onChange={e => setPersonForm({ ...personForm, titre_sejour: e.target.value })} /></div>
                <div><Label>Expiration titre de séjour</Label><Input type="date" value={personForm.expiration_titre_sejour} onChange={e => setPersonForm({ ...personForm, expiration_titre_sejour: e.target.value })} /></div>
              </div>
            </TabsContent>

            <TabsContent value="birth" className="mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label>Date de naissance</Label><Input value={personForm.date_naissance} placeholder="JJ/MM/AAAA" onChange={e => setPersonForm({ ...personForm, date_naissance: e.target.value })} /></div>
                <div><Label>Lieu de naissance</Label><Input value={personForm.lieu_naissance} onChange={e => setPersonForm({ ...personForm, lieu_naissance: e.target.value })} /></div>
                <div><Label>Département de naissance</Label><Input value={personForm.departement_naissance} onChange={e => setPersonForm({ ...personForm, departement_naissance: e.target.value })} /></div>
                <div><Label>Pays de naissance</Label><Input list="countries-list" value={personForm.libelle_pays_naissance} onChange={e => setPersonForm({ ...personForm, libelle_pays_naissance: e.target.value.toUpperCase() })} /></div>
                <div className="md:col-span-2"><Label>N° de sécurité sociale</Label><Input value={personForm.numero_ss} onChange={e => setPersonForm({ ...personForm, numero_ss: e.target.value })} data-testid="p-ss" /></div>
                <div className="md:col-span-2"><Label>N° Congés Spectacles</Label><Input value={personForm.numero_conges_spectacles} onChange={e => setPersonForm({ ...personForm, numero_conges_spectacles: e.target.value })} /></div>
              </div>
            </TabsContent>

            <TabsContent value="paye" className="mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label>Code société</Label><Input value={personForm.code_societe} onChange={e => setPersonForm({ ...personForm, code_societe: e.target.value })} /></div>
                <div><Label>Raison sociale</Label><Input value={personForm.raison_sociale} onChange={e => setPersonForm({ ...personForm, raison_sociale: e.target.value })} /></div>
                <div><Label>Type contrat</Label><Input value={personForm.type_contrat} onChange={e => setPersonForm({ ...personForm, type_contrat: e.target.value })} /></div>
                <div><Label>Fiscalité</Label><Input value={personForm.fiscalite} onChange={e => setPersonForm({ ...personForm, fiscalite: e.target.value })} /></div>
                <div><Label>Abattement</Label><Input value={personForm.abattement} onChange={e => setPersonForm({ ...personForm, abattement: e.target.value })} /></div>
                <div><Label>Code dernier contrat</Label><Input value={personForm.code_dernier_contrat} onChange={e => setPersonForm({ ...personForm, code_dernier_contrat: e.target.value })} /></div>
                <div><Label>Code profession</Label><Input value={personForm.code_profession} onChange={e => setPersonForm({ ...personForm, code_profession: e.target.value })} /></div>
                <div><Label>Date visite médicale</Label><Input value={personForm.date_visite_medicale} placeholder="JJ/MM/AAAA" onChange={e => setPersonForm({ ...personForm, date_visite_medicale: e.target.value })} /></div>
                <div><Label>Prochaine visite médicale</Label><Input value={personForm.date_limite_prochaine_visite} placeholder="JJ/MM/AAAA" onChange={e => setPersonForm({ ...personForm, date_limite_prochaine_visite: e.target.value })} /></div>
                <div><Label>+65 ans</Label>
                  <Select value={personForm.plus_65_ans} onValueChange={v => setPersonForm({ ...personForm, plus_65_ans: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Non">Non</SelectItem><SelectItem value="Oui">Oui</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Retraité</Label>
                  <Select value={personForm.retraite} onValueChange={v => setPersonForm({ ...personForm, retraite: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Non">Non</SelectItem><SelectItem value="Oui">Oui</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Nb enfants à charge</Label><Input type="number" min="0" value={personForm.nb_enfants} onChange={e => setPersonForm({ ...personForm, nb_enfants: e.target.value })} /></div>
                <div><Label>Refus dématérialisation</Label>
                  <Select value={personForm.refus_demat} onValueChange={v => setPersonForm({ ...personForm, refus_demat: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Non">Non</SelectItem><SelectItem value="Oui">Oui</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="bank" className="mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label>Mode de règlement</Label><Input value={personForm.mode_reglement} onChange={e => setPersonForm({ ...personForm, mode_reglement: e.target.value })} /></div>
                <div><Label>IBAN</Label><Input value={personForm.iban} onChange={e => setPersonForm({ ...personForm, iban: e.target.value })} placeholder="FR76..." /></div>
                <div><Label>BIC</Label><Input value={personForm.bic} onChange={e => setPersonForm({ ...personForm, bic: e.target.value })} /></div>
                <div><Label>Niveau de confidentialité</Label><Input value={personForm.niveau_confidentialite} onChange={e => setPersonForm({ ...personForm, niveau_confidentialite: e.target.value })} /></div>
                <div><Label>Compte racine</Label><Input value={personForm.compte_racine} onChange={e => setPersonForm({ ...personForm, compte_racine: e.target.value })} /></div>
                <div><Label>Compte terminaison</Label><Input value={personForm.compte_terminaison} onChange={e => setPersonForm({ ...personForm, compte_terminaison: e.target.value })} /></div>
                <div><Label>Libellé racine</Label><Input value={personForm.libelle_racine} onChange={e => setPersonForm({ ...personForm, libelle_racine: e.target.value })} /></div>
                <div><Label>Libellé terminaison</Label><Input value={personForm.libelle_terminaison} onChange={e => setPersonForm({ ...personForm, libelle_terminaison: e.target.value })} /></div>
              </div>
            </TabsContent>

            <TabsContent value="docs" className="mt-4">
              <div className="space-y-3">
                <div className="text-xs text-muted-foreground">Documents fournis par l'intermittent via son formulaire.</div>
                {[
                  { key: "doc_carte_vitale_id", label: "Recto carte vitale" },
                  { key: "doc_rib_id", label: "RIB" },
                  { key: "doc_attestation_medicale_id", label: "Attestation visite médicale" },
                ].map(d => {
                  const fid = personForm[d.key];
                  return (
                    <div key={d.key} className="flex items-center gap-3 border border-border/60 rounded-lg p-3">
                      <div className="flex-1">
                        <div className="text-sm font-medium">{d.label}</div>
                        <div className="text-xs text-muted-foreground font-mono truncate">{fid || "Aucun document"}</div>
                      </div>
                      {fid && (
                        <Button variant="outline" size="sm" className="rounded-full" onClick={async () => {
                          try {
                            const res = await api.get(`/files/${fid}`, { responseType: "blob" });
                            const url = window.URL.createObjectURL(new Blob([res.data]));
                            window.open(url, "_blank");
                          } catch (e) { toast.error("Fichier introuvable"); }
                        }} data-testid={`open-${d.key}`}>Ouvrir</Button>
                      )}
                    </div>
                  );
                })}
                <div className="flex items-center gap-2 pt-2">
                  <Checkbox id="med-en-attente" checked={!!personForm.visite_medicale_en_attente} onCheckedChange={v => setPersonForm({ ...personForm, visite_medicale_en_attente: !!v })} />
                  <Label htmlFor="med-en-attente" className="cursor-pointer text-sm">Visite médicale en attente de rendez-vous</Label>
                </div>
              </div>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setPersonDialogOpen(false)}>Annuler</Button>
            <Button className="rounded-full active:scale-95 bg-primary" onClick={savePerson} data-testid="confirm-person">{personEditingId ? "Enregistrer" : "Créer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Public links dialog (registration) */}
      <Dialog open={publicOpen} onOpenChange={setPublicOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Link2 className="h-5 w-5" /> Liens publics d'inscription</DialogTitle></DialogHeader>
          <div className="text-sm text-muted-foreground bg-muted rounded-lg p-3 mb-3">
            Créez un lien à envoyer par email/SMS à un intermittent. Il ouvre un formulaire simple sans besoin de compte pour compléter sa fiche.
          </div>
          <div className="border border-border/60 rounded-lg p-4 space-y-3 mb-4">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Nouveau lien d'inscription</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Libellé (interne)</Label>
                <Input value={newLinkForm.label} onChange={e => setNewLinkForm({ ...newLinkForm, label: e.target.value })} placeholder="Inscription 2026" data-testid="link-label" />
              </div>
              <div>
                <Label>Intermittent pré-lié (optionnel)</Label>
                <Select value={newLinkForm.intermittent_id || "none"} onValueChange={v => setNewLinkForm({ ...newLinkForm, intermittent_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="link-intermittent"><SelectValue placeholder="Nouveau" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nouveau (client saisit)</SelectItem>
                    {intermittents.map(p => <SelectItem key={p.id} value={p.id}>{formatIntermittentName(p)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>URL personnalisée (optionnel)</Label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground shrink-0 font-mono">…/declaration/</span>
                  <Input
                    value={newLinkForm.slug}
                    onChange={e => setNewLinkForm({ ...newLinkForm, slug: e.target.value })}
                    placeholder="inscription-2026"
                    data-testid="link-slug"
                  />
                </div>
                <div className="text-[10px] text-muted-foreground mt-1">Lettres minuscules, chiffres et tirets (3 à 60 caractères). Laissez vide pour un token aléatoire.</div>
              </div>
              <div className="md:col-span-2 flex items-center gap-2">
                <Checkbox id="link-active" checked={newLinkForm.active} onCheckedChange={v => setNewLinkForm({ ...newLinkForm, active: !!v })} />
                <Label htmlFor="link-active" className="cursor-pointer text-sm">Lien actif</Label>
              </div>
            </div>
            <Button className="rounded-full bg-primary active:scale-95" onClick={createPublicLink} data-testid="create-link-btn"><Plus className="h-4 w-4 mr-2" /> Créer & copier le lien</Button>
          </div>

          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Liens actifs</div>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {publicLinks.filter(l => l.type !== "declaration_request").length === 0 && <div className="text-sm text-muted-foreground text-center py-6">Aucun lien</div>}
            {publicLinks.filter(l => l.type !== "declaration_request").map(l => (
                <div key={l.token} className="border border-border/60 rounded-lg p-3 flex items-center gap-3" data-testid={`link-row-${l.token}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate flex items-center gap-2">
                      {l.label || "Sans libellé"}
                      {!l.active && <Badge variant="outline" className="text-[10px] rounded-full text-destructive border-destructive/40">Inactif</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{l.intermittent ? formatIntermittentName(l.intermittent) : "Formulaire libre"} · {l.used_count || 0} soumission(s)</div>
                    <div className="text-[11px] text-muted-foreground truncate font-mono">{l.url}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox checked={!!l.active} onCheckedChange={() => toggleLinkActive(l)} data-testid={`toggle-${l.token}`} />
                  </div>
                  <Button variant="outline" size="icon" className="rounded-full" onClick={() => editSlug(l)} title="Modifier l'URL personnalisée" data-testid={`slug-${l.token}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="outline" size="icon" className="rounded-full" onClick={() => copyLink(l.url)} data-testid={`copy-${l.token}`}><Copy className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="text-destructive hover:bg-destructive/10" onClick={() => removePublicLink(l.token)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setPublicOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <datalist id="countries-list">
        {COUNTRIES.map(c => (
          <option key={c.code} value={c.name} />
        ))}
      </datalist>

      {/* Dialogue "Lien public" — inscription intermittent */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="inscription-link-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Link2 className="h-5 w-5 text-primary" /> Formulaire d'inscription intermittent</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg bg-muted border border-border/60 p-3">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Lien public</Label>
              <div className="mt-1 font-mono text-sm break-all text-primary">{INSCRIPTION_URL}</div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1 rounded-full border-primary/40 text-primary hover:bg-primary/10"
                onClick={() => copyLink(INSCRIPTION_URL)}
                data-testid="copy-inscription-link"
              >
                <Copy className="h-4 w-4 mr-2" /> Copier le lien
              </Button>
              <Button
                variant="outline"
                className="flex-1 rounded-full"
                onClick={() => window.open(INSCRIPTION_URL, "_blank")}
                data-testid="open-inscription-link"
              >
                <ExternalLink className="h-4 w-4 mr-2" /> Ouvrir
              </Button>
            </div>
            <div className="border-t border-border/60 pt-4">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Envoyer par email</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  type="email"
                  placeholder="destinataire@exemple.fr"
                  value={emailTo}
                  onChange={e => setEmailTo(e.target.value)}
                  data-testid="inscription-email-input"
                />
                <Button
                  className="rounded-full bg-primary active:scale-95"
                  onClick={sendByEmail}
                  data-testid="send-inscription-email"
                >
                  <Mail className="h-4 w-4 mr-2" /> Envoyer
                </Button>
              </div>
              <div className="text-[11px] text-muted-foreground mt-2">Email envoyé depuis <strong>contact@antoineevenements.fr</strong> avec le lien et un message d'invitation.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setLinkDialogOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
